import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {Observable, map, of, forkJoin, catchError} from 'rxjs';

import { environment } from '../../../environments/environment';

import { SessionStorageService } from './session-storage.service';
import { Student, StudentAdapter } from '../models/student.model';
import { UtilityService } from './utility.service';
import { StudentSession } from '../models/student-session.model';
import { StudentSessionSummary } from '../models/student-session-summary.model';

@Injectable({
  providedIn: 'root'
})
export class StudentsService {

  constructor(
    private studentAdapter: StudentAdapter,
    private httpClient: HttpClient,
    private sessionStorageService: SessionStorageService,
    private utilityService: UtilityService,
  ) {}

  getStudentInformation(userID: number) {
    const reqOpts = {
      withCredentials: true,
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    }

    return this.httpClient.get(environment.dataServicesURL + 'manage/enroll/student/' + userID, reqOpts);
  }

  /**
   * Makes a network call to get the student list for either the current user or the entire school based
   * on the scope parameter.
   *
   * @param scope 'mine' or 'school'
   * @returns observable list of students
   */
  getStudents(scope: 'mine' | 'school' = 'mine'): Observable<Student[]> {
    const reqOpts = {
      withCredentials: true,
    };

    const url = scope === 'mine'
      ? `${environment.dataServicesURL}manage/enroll/student/list`
      : `${environment.dataServicesURL}manage/enroll/school/student/list`;

    return this.httpClient.get<Student[]>(url, reqOpts).pipe(
      map((data: any) => {
        let students: Student[] = [];

        data.forEach((student: any) => {
          students.push(this.studentAdapter.adapt(student));
        })

        return students;
      })
    );
  }

  archiveStudents(studentIds: number[]): Observable<any> {
    const reqOpts = {
      withCredentials: true,
      observe: 'response',
    };

    let currentUser = this.sessionStorageService.userData() ;
    if (!currentUser?.userID) throw new Error('Could not load current user');

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/archive`, studentIds, reqOpts as any);
  }

  getCoTeacherAccessData(): Observable<Student[]> {
    const reqOpts = {
      withCredentials: true,
    };

    let currentUser = this.sessionStorageService.userData() ;
    if (!currentUser?.isTeacherUser())
    {
      return of([]) ;
    }

    return this.httpClient.get<Student[]>(`${environment.dataServicesURL}manage/enroll/coteacher/list`, reqOpts).pipe(
      map((data: any) => {
        let students: Student[] = [];

        data.forEach((student: any) => {
          students.push(this.studentAdapter.adapt(student));
        })

        return students;
      })
    ) ;
  }

  /**
   * Gets co-teacher data for a specific teacher. If no teacherID is provided,
   * then the data will be for the calling user. Returns an object with keys of
   * the co-teacher IDs and values of arrays of student IDs that the co-teacher
   * has access to.
   */
  getGrantedCoTeacherData(teacherID?: number): Observable<Record<number, number[]>> {
    const reqOpts = {
      withCredentials: true,
    };

    const userID = teacherID ?? this.sessionStorageService.userData()?.userID;
    return this.httpClient.get<Record<number, number[]>>(
      `${environment.dataServicesURL}manage/enroll/teacher/${userID}/coteacher/granted`,
      reqOpts
    ) ;
  }

  assignCoTeacherAccess(primaryTeacherId: number, coTeacherId: number, studentIds?: number[]): Observable<{ [key: number]: (number | null)[] }> {
    const reqOpts = {
      withCredentials: true,
      observe: 'response',
    };

    // Add a co-teacher for all or some of the current users students
    let url = '' ;
    let payload = null ;

    if (!studentIds || studentIds.length === 0)
    {
      url = `${environment.dataServicesURL}manage/enroll/teacher/${primaryTeacherId}/coteacher/${coTeacherId}` ;
    }
    else
    {
      url = `${environment.dataServicesURL}manage/enroll/teacher/${primaryTeacherId}/coteacher/${coTeacherId}/students` ;
      payload = { studentIds } ;
    }

    return this.httpClient.put<Observable<{ [key: number]: (number | null)[] }>>(url, payload, reqOpts as any).pipe(
      map((data: any) => {
        return data.body ;
      }),
      catchError(error => of(error.status))
    ) ;
  }

  revokeCoTeacherAccess(primaryTeacherId: number, coTeacherId: number, studentId?: number): Observable<any> {
    const reqOpts = {
      withCredentials: true,
      observe: 'response',
    };

    const url = (!studentId) ?
      `${environment.dataServicesURL}manage/enroll/teacher/${primaryTeacherId}/coteacher/${coTeacherId}/revoke`
      : `${environment.dataServicesURL}manage/enroll/teacher/${primaryTeacherId}/coteacher/${coTeacherId}/student/${studentId}/revoke` ;

    return this.httpClient.put(url, null, reqOpts as any).pipe(
      map((data: any) => {
        return data.body ;
      }),
      catchError(error => of(error.status))
    ) ;
  }

  cancelStudentSubscriptions(studentIds: number[]): Observable<any> {
    const reqOpts = {
      withCredentials: true,
      observe: 'response',
    };

    // Transform our student IDs into observables that can be called
    let requests = studentIds.map((studentId) => {
      return this.httpClient.post(`${environment.dataServicesURL}manage/enroll/student/${studentId}/cancel-subscription`, null, reqOpts as any).pipe(
        map((data: any) => {
          return data.status ;
        }),
        catchError(error => of(error.status))
      ) ;
    }) ;

    return forkJoin(requests) ;
  }

  filterStudents(students: Student[], filters: any) : Student[] {
    return students.filter((student) => this.studentMeetsCriteria(student, filters)) ;
  }

  filterStudentSessions(sessions: StudentSession[], filters: any): StudentSession[] {
    return sessions.filter((session) => this.studentMeetsCriteria(session.student, filters)) ;
  }

  filterCoTeacherSessionSummary(sessions: StudentSessionSummary[], teacherID: number, hideSharedStudents: boolean): StudentSessionSummary[] {
    if (hideSharedStudents)
    {
      return sessions.filter((session) => session.schoolClass?.teacherID === teacherID) ;
    }
    else
    {
      return sessions ;
    }
  }

  filterStudentSummarySessions(sessions: StudentSessionSummary[], filters: any): StudentSessionSummary[] {
    return sessions.filter((session) => this.studentMeetsCriteria(session, filters)) ;
  }

  isParentEnabled(userId: number | undefined): Observable<boolean> {
    if (!userId) return of(false) ;

    const reqOpts = {
      withCredentials: true,
    };

    return this.httpClient.get<boolean>(`${environment.dataServicesURL}manage/enroll/parent/${userId}/enabled`, reqOpts).pipe(
      catchError((error) => {
        console.log(error) ;
        return of(false) ;
      })
    ) ;
  }

  private studentMeetsCriteria(student: any, filters: any): boolean {
    if (!filters) return true;

    const gradeFilter = filters.grade?.toLowerCase().trim() || 'all';
    const classFilter = parseInt(filters.class) || 0;
    const extraSupportFilter = filters.extraSupport?.toLowerCase().trim() || 'all';

    // 1. Determine if Grade matches (including special logic)
    let gradeAllowed = false;

    if (gradeFilter === 'all') {
      gradeAllowed = true;
    } else if (gradeFilter === 'four_below_all') {
      gradeAllowed = student.grade <= 5;
    } else if (gradeFilter === 'five_up_all') {
      gradeAllowed = student.grade > 5;
    } else if (gradeFilter.toLowerCase() === 'k') {
      gradeAllowed = student.grade === 1;
    } else if (gradeFilter.toLowerCase() === 'other') {
      gradeAllowed = student.grade === 14;
    } else {
      gradeAllowed = (gradeFilter === this.utilityService.getStudentGrade(student.grade));
    }

    // 2. Determine if Class matches
    const classAllowed = (classFilter === 0 || classFilter === student.schoolClass?.schoolClassID);

    // 3. Determine if Extra Support matches
    const extraSupportAllowed = (
      extraSupportFilter === 'all' ||
      (extraSupportFilter === 'no' && !student.extraSupportNeeded) ||
      (extraSupportFilter === 'yes' && student.extraSupportNeeded)
    );

    return gradeAllowed && classAllowed && extraSupportAllowed ;
  }
}
